import { ref, shallowRef } from 'vue'
import speakerTestAudio from '@/assets/soundTest.mp3'
import { useCameraStore } from '@/store/camera'

interface StartAudioDiagnosticsParams {
  stream?: MediaStream
  onLevelChange?: (level: number) => void
}

const DEFAULT_MIC_ERROR = '未检测到可用的麦克风音轨，请检查设备权限'

async function waitForCameraStream(getStream: () => MediaStream | undefined, timeout = 2000) {
  const startAt = Date.now()

  while (Date.now() - startAt < timeout) {
    const stream = getStream()
    const videoTrack = stream?.getVideoTracks?.()[0]

    if (stream && videoTrack && videoTrack.readyState === 'live') {
      return stream
    }

    await new Promise((resolve) => setTimeout(resolve, 50))
  }

  return getStream()
}

export function useTencentDeviceTest() {
  const cameraStore = useCameraStore()
  const audioContext = shallowRef<AudioContext>()
  const analyser = shallowRef<AnalyserNode>()
  const micSource = shallowRef<MediaStreamAudioSourceNode>()
  const speakerAudio = shallowRef<HTMLAudioElement>()
  const micFrame = ref<number>()

  const stopMicLevelWatch = () => {
    if (micFrame.value) {
      cancelAnimationFrame(micFrame.value)
      micFrame.value = undefined
    }
  }

  const cleanupAudioDiagnostics = () => {
    stopMicLevelWatch()
    micSource.value?.disconnect()
    analyser.value?.disconnect()
    micSource.value = undefined
    analyser.value = undefined

    if (audioContext.value && audioContext.value.state !== 'closed') {
      audioContext.value.close().catch(() => undefined)
    }
    audioContext.value = undefined
  }

  const getCameraDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((item) => item.kind === 'videoinput')
  }

  const ensureCameraReady = async (deviceId?: string) => {
    if (deviceId) {
      cameraStore.currentCamera = deviceId
    }

    await cameraStore.startCamera()
    const stream = await waitForCameraStream(() => cameraStore.stream as MediaStream | undefined)
    const devices = await getCameraDevices()

    return {
      devices,
      selectedDeviceId: (cameraStore.currentCamera || devices[0]?.deviceId || '') as string,
      stream
    }
  }

  const changeCamera = async (deviceId: string) => {
    cameraStore.currentCamera = deviceId
    await cameraStore.restartCamera()
    const stream = await waitForCameraStream(() => cameraStore.stream as MediaStream | undefined)
    const devices = await getCameraDevices()

    return {
      devices,
      selectedDeviceId: deviceId,
      stream
    }
  }

  const startAudioDiagnostics = async ({
    stream,
    onLevelChange
  }: StartAudioDiagnosticsParams = {}) => {
    cleanupAudioDiagnostics()

    const sourceStream = stream || (cameraStore.stream as MediaStream | undefined)
    const audioTrack = sourceStream?.getAudioTracks?.()[0]

    if (!audioTrack) {
      throw new Error(DEFAULT_MIC_ERROR)
    }

    const audioOnlyStream = new MediaStream([audioTrack])
    const context = new AudioContext()
    const analyserNode = context.createAnalyser()
    analyserNode.fftSize = 512
    const mediaSource = context.createMediaStreamSource(audioOnlyStream)
    mediaSource.connect(analyserNode)

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount)

    const updateLevel = () => {
      analyserNode.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length
      onLevelChange?.(Math.min(100, Math.round((average / 255) * 100)))
      micFrame.value = requestAnimationFrame(updateLevel)
    }

    audioContext.value = context
    analyser.value = analyserNode
    micSource.value = mediaSource
    updateLevel()

    return true
  }

  const playSpeakerTest = async () => {
    if (!speakerAudio.value) {
      speakerAudio.value = new Audio(speakerTestAudio)
    }

    speakerAudio.value.currentTime = 0
    await speakerAudio.value.play()
  }

  const stopSpeakerTest = () => {
    if (!speakerAudio.value) return

    speakerAudio.value.pause()
    speakerAudio.value.currentTime = 0
  }

  const cleanup = () => {
    cleanupAudioDiagnostics()
    stopSpeakerTest()
  }

  return {
    getCameraDevices,
    ensureCameraReady,
    changeCamera,
    startAudioDiagnostics,
    playSpeakerTest,
    stopSpeakerTest,
    cleanupAudioDiagnostics,
    cleanup
  }
}
